import type {
  BatchContext,
  CaptureEvent,
  CustomEvent,
  ExceptionContext,
  ExtraitBatch,
  RecordingBatch,
  RrwebEvent,
} from '@belvdr/shared'
import { record } from '@rrweb/record'
import { consolePlugin } from './console.js'
import {
  createExceptionReporter,
  type BeforeSendException,
  type ExceptionReporter,
} from './exception-reporter.js'
import { parseThrown, watchExceptions, type ParsedException } from './exceptions.js'
import { createExtraitRecorder, type Extrait, type ExtraitRecorder } from './extrait-recorder.js'
import { interactionsPlugin } from './interactions.js'
import { BLOCK_CLASS, MASK_CLASS } from './masking.js'
import { networkPlugin } from './network.js'
import type { MaskNetworkBody } from './network-body.js'
import type { MaskNetworkUrl } from './network-url.js'
import {
  createSession,
  identityOf,
  isActive,
  isInteraction,
  localSessionStore,
  newIdentity,
  type Identity,
  type SessionStore,
  type SharedSession,
} from './session.js'
import { createSender, type Sender } from './sender.js'
import { browserTab, type BrowserTab } from './tab.js'
import { fetchTransport, type Transport } from './transport.js'
import { uuidv7 } from './uuid.js'
import { SDK_VERSION } from './version.js'

/** Chemin de l'endpoint d'ingestion (cf. `RECORDINGS_PATH` de `@belvdr/shared`). */
const RECORDINGS_PATH = '/ingest/recordings'
/** Chemin de l'endpoint des Exceptions (cf. `EXCEPTIONS_PATH` de `@belvdr/shared`). */
const EXCEPTIONS_PATH = '/ingest/exceptions'
/** Chemin de l'endpoint des Extraits (cf. `EXTRAITS_PATH` de `@belvdr/shared`). */
const EXTRAITS_PATH = '/ingest/extraits'
/** Fréquence d'envoi des événements accumulés (cf. PRD). */
const FLUSH_INTERVAL_MS = 5_000
/** Volume accumulé (JSON) qui déclenche un envoi sans attendre l'intervalle (cf. PRD). */
const FLUSH_THRESHOLD_BYTES = 500 * 1024
/** Full snapshot périodique : point de départ du lecteur lors d'un saut dans la timeline. */
const FULL_SNAPSHOT_INTERVAL_MS = 5 * 60_000
/** Event d'identification (cf. `IDENTIFY_EVENT` de `@belvdr/shared`). */
const IDENTIFY_EVENT = '$identify'
/** Longueur maximale d'un `userId` (cf. `externalIdSchema` de `@belvdr/shared`). */
const MAX_USER_ID_LENGTH = 200
/** Type rrweb des événements personnalisés (`EventType.Custom`), visibles dans l'enregistrement. */
const RRWEB_CUSTOM_EVENT = 5
/** Longueur maximale du nom d'un event (cf. `MAX_EVENT_NAME_LENGTH` de `@belvdr/shared`). */
const MAX_EVENT_NAME_LENGTH = 200
/** Taille maximale des propriétés en JSON (cf. `MAX_EVENT_PROPERTIES_LENGTH`). */
const MAX_EVENT_PROPERTIES_LENGTH = 10_000
/** Events custom au plus par batch (cf. `MAX_CUSTOM_EVENTS_PER_BATCH`). */
const MAX_CUSTOM_EVENTS_PER_BATCH = 100

/** Classes CSS de masquage proposées aux intégrateurs. */
export { BLOCK_CLASS, MASK_CLASS }

export type BelvdrOptions = {
  /** Origine de l'API belvdr, par exemple `https://belvdr.example.com`. */
  apiHost: string
  /**
   * Masque les arguments de la console (chaque argument devient `***`, message des exceptions
   * compris) : à activer si l'application logue des données personnelles. Le niveau, l'instant
   * et la pile d'appels restent enregistrés. Le message des Exceptions est masqué de même : elles
   * restent regroupées en Issues par leur type et leur pile.
   */
  maskConsole?: boolean
  /**
   * Capture du réseau : requêtes `fetch` et `XMLHttpRequest` (méthode, URL, statut, durée,
   * taille, échec) et ressources statiques, sans en-tête ni corps. `false` la désactive.
   */
  captureNetwork?: boolean
  /**
   * Relevé des Faits pendant une session : les Actions du visiteur (clic sur un élément
   * actionnable, envoi d'un formulaire) avec la Signature de l'élément (rôle, libellé, identifiant
   * stable, titre de son conteneur), jamais de valeur saisie. Ils permettent à belvdr d'apprendre
   * le comportement normal de l'application et d'en signaler les bugs silencieux. `false` le
   * désactive.
   */
  captureInteractions?: boolean
  /**
   * Réécrit l'URL (absolue) d'une requête avant son enregistrement, ou renvoie `null` pour
   * l'ignorer. Les valeurs des paramètres sensibles (`token`, `key`, `password`, `email`…) sont
   * masquées dans tous les cas, après ce hook.
   */
  maskNetworkUrl?: MaskNetworkUrl
  /**
   * Capture les corps (requête et réponse) des requêtes `fetch` et `XMLHttpRequest` en erreur
   * (4xx / 5xx), pour déboguer les erreurs serveur. Désactivée par défaut. Garde-fous toujours
   * actifs : corps textuels uniquement, valeurs des clés sensibles (`password`, `token`,
   * `secret`, `authorization`, `card`, `iban`…) masquées à toute profondeur, 10 Ko au plus.
   */
  captureErrorBodies?: boolean
  /**
   * Réécrit le corps (texte, tel qu'envoyé ou reçu) d'une requête en erreur avant son
   * enregistrement, ou renvoie `null` pour ne pas l'enregistrer (`captureErrorBodies`). Reçoit
   * aussi `{ kind: 'request' | 'response', url, method, status }`. Les valeurs des clés
   * sensibles sont masquées et le corps limité à 10 Ko dans tous les cas, après ce hook.
   */
  maskNetworkBody?: MaskNetworkBody
  /**
   * Réécrit une Exception avant son envoi (type, message, frames, URL), ou renvoie `null` pour
   * l'écarter : retirer une donnée personnelle d'un message, ignorer les erreurs d'un script tiers.
   * Un hook qui lève une erreur écarte l'Exception, avec un avertissement en console. Les
   * paramètres sensibles de l'URL sont masqués dans tous les cas, après ce hook.
   */
  beforeSendException?: BeforeSendException
  /** Envoi des batchs (par défaut `fetch`) : à remplacer uniquement pour les tests. */
  transport?: Transport
}

export type StartSessionOptions = {
  /** Termine la session en cours et en démarre une nouvelle. */
  forceNew?: boolean
  /** Identifie l'utilisateur dès le démarrage, comme `identify(userId)`. */
  userId?: string
}

export type Belvdr = {
  /** Configure le SDK. N'enregistre rien : l'enregistrement démarre avec `startSession()`. */
  init(publicKey: string, options: BelvdrOptions): void
  /**
   * Démarre l'enregistrement (typiquement après le consentement), à appeler à chaque chargement
   * de page : reprend la session active du navigateur (partagée entre onglets) dans une nouvelle
   * window, sinon en crée une. `forceNew` démarre une nouvelle session dans tous les cas.
   */
  startSession(options?: StartSessionOptions): void
  /**
   * Arrête l'enregistrement dans tous les onglets (retrait du consentement) et envoie les
   * derniers événements. Le prochain `startSession()` démarrera une nouvelle session.
   */
  stopSession(): void
  /**
   * Démarre le Mode erreurs (typiquement après le consentement), à appeler à chaque chargement
   * de page : les Exceptions (exceptions non gérées, promesses rejetées, `captureException`)
   * sont envoyées sans enregistrer de session complète. Rien n'est capturé avant.
   */
  startErrorTracking(): void
  /** Arrête le Mode erreurs dans tous les onglets (retrait du consentement). */
  stopErrorTracking(): void
  /**
   * Rattache ce navigateur à l'utilisateur `userId` (son identifiant dans ton système), après
   * son login : la session en cours et l'historique anonyme du navigateur lui reviennent. Si le
   * navigateur appartient déjà à un autre utilisateur, une nouvelle identité et une nouvelle
   * session commencent (jamais de fusion). Avant `startSession()`, rien n'est enregistré :
   * l'identification s'applique au démarrage.
   */
  identify(userId: string): void
  /**
   * Au logout : nouveau `distinctId` anonyme et nouvelle session, pour que l'utilisateur suivant
   * ne soit pas confondu avec le précédent.
   */
  reset(): void
  /**
   * Marque un moment clé du parcours (`name`, avec des `properties` facultatives : un objet
   * sérialisable en JSON, 10 000 caractères au plus) : il apparaît sur la timeline du replay et
   * permet de filtrer les sessions. Ignoré quand rien n'est enregistré (avant `startSession()`,
   * après `stopSession()`, session expirée). Les noms commençant par `$` sont réservés.
   */
  capture(name: string, properties?: Record<string, unknown>): void
  /**
   * Envoie une erreur que l'application a attrapée, comme Exception gérée : elle rejoint son
   * Issue dans le dashboard. Accepte n'importe quelle valeur (une `Error`, une chaîne, un objet) ;
   * ne fait rien quand rien n'est enregistré (avant `startSession()`, après `stopSession()`) et ne
   * lève jamais d'erreur.
   */
  captureException(error: unknown): void
}

type Config = {
  publicKey: string
  apiHost: string
  sender: Sender
  /** Envoi des Exceptions, à leur propre endpoint, après leurs garde-fous. */
  exceptions: ExceptionReporter
  /** Envoi des batchs d'Extraits, à leur propre endpoint. */
  extraitSender: Sender
  store: SessionStore
  maskConsole: boolean
  captureNetwork: boolean
  captureInteractions: boolean
  maskNetworkUrl: MaskNetworkUrl | undefined
  captureErrorBodies: boolean
  maskNetworkBody: MaskNetworkBody | undefined
  /** L'API refuse l'origine de cette page : plus rien n'y est enregistré jusqu'au rechargement. */
  originRefused: boolean
  /** Onglet du navigateur de la page (`tabId` des batchs). */
  tab: BrowserTab
}

/** Une window : l'enregistrement de cet onglet dans une session donnée. */
type RecordedWindow = {
  /** Identité du navigateur pendant cette window (celle de sa session). */
  distinctId: string
  sessionId: string
  sessionStartedAt: number
  windowId: string
  nextSeq: number
  buffer: RrwebEvent[]
  /** Events custom (`$identify`, `capture()`) qui partiront avec le prochain envoi. */
  customEvents: CustomEvent[]
  /** Taille approximative (JSON) des événements du tampon. */
  bufferBytes: number
  /** Session créée par cet onglet pour cette window (et non reprise ou rejointe). */
  createdHere: boolean
  /** Session refusée par l'API (trop volumineuse) : plus rien n'est enregistré. */
  capped: boolean
}

/** Enregistrement en cours dans cet onglet. */
type Recording = {
  /** Identité courante de l'onglet, alignée sur l'état partagé. */
  identity: Identity
  window: RecordedWindow
  /** Dernière interaction connue, dans cet onglet ou (après synchronisation) dans un autre. */
  lastActivityAt: number
  stopped: boolean
  stop: () => void
}

/** Contexte de la page joint à une Exception (son URL est à part, nettoyée). */
function exceptionContext(): ExceptionContext {
  return {
    userAgent: navigator.userAgent,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: { width: window.innerWidth, height: window.innerHeight },
  }
}

function currentContext(): BatchContext {
  return {
    url: location.href,
    referrer: document.referrer,
    userAgent: navigator.userAgent,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: { width: window.innerWidth, height: window.innerHeight },
  }
}

type CaptureProperties = CaptureEvent['properties']

/**
 * Propriétés d'un event, ramenées à ce que le JSON en garde (une date devient une chaîne, une
 * valeur `undefined` disparaît), ou la raison de leur refus.
 */
function captureProperties(properties: unknown): CaptureProperties | { refused: string } {
  if (properties === undefined) return {}
  if (typeof properties !== 'object' || properties === null || Array.isArray(properties))
    return { refused: 'un objet est attendu' }
  let json: string
  try {
    json = JSON.stringify(properties)
  } catch {
    return { refused: 'elles ne sont pas sérialisables en JSON' }
  }
  if (json.length > MAX_EVENT_PROPERTIES_LENGTH)
    return { refused: `plus de ${MAX_EVENT_PROPERTIES_LENGTH} caractères en JSON` }
  const parsed: unknown = JSON.parse(json)
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
    return { refused: 'un objet est attendu' }
  return parsed as CaptureProperties
}

/** Raison du refus d'un nom d'event, `null` s'il est valide. */
function refusedEventName(name: unknown): string | null {
  if (typeof name !== 'string' || name.length === 0 || name.length > MAX_EVENT_NAME_LENGTH)
    return `une chaîne de 1 à ${MAX_EVENT_NAME_LENGTH} caractères est attendue`
  if (name.startsWith('$')) return 'les noms commençant par « $ » sont réservés'
  return null
}

function warnRefusedEvent(name: unknown, reason: string) {
  console.warn(`[belvdr] event ${JSON.stringify(name)} ignoré : ${reason}.`)
}

function isValidUserId(userId: unknown): userId is string {
  return typeof userId === 'string' && userId.length > 0 && userId.length <= MAX_USER_ID_LENGTH
}

function warnInvalidUserId(userId: unknown) {
  console.warn(
    `[belvdr] userId ignoré : une chaîne de 1 à ${MAX_USER_ID_LENGTH} caractères est attendue ` +
      `(reçu ${JSON.stringify(userId)}).`,
  )
}

export function createBelvdr(): Belvdr {
  let config: Config | null = null
  let recording: Recording | null = null
  /** `identify()` appelé alors que cet onglet n'enregistre pas : appliqué au démarrage. */
  let pendingUserId: string | undefined
  /**
   * L'utilisateur en attente a été identifié en Mode erreurs, sans session : l'identité partagée
   * le connaît déjà, mais le prochain `startSession()` doit tout de même émettre son `$identify`.
   */
  let identifiedWithoutSession = false
  /**
   * Mode erreurs démarré dans cet onglet : son arrêt (ici ou dans un autre onglet) le retire.
   * `buffer` : l'enregistrement de la page dans le tampon des Extraits, arrêté pendant une session
   * complète (elle a son propre enregistrement).
   */
  let errorTracking: {
    identity: Identity
    stop: () => void
    buffer: { extraits: ExtraitRecorder; stop: () => void } | null
  } | null = null
  /** Navigateur de chaque Extrait en cours (celui de son Exception). */
  const extraitOwners = new Map<string, string>()
  /** Exceptions de la page, écoutées tant qu'une session ou le Mode erreurs tourne. */
  let unwatch: (() => void) | null = null

  /** Envoie le tampon de la window ; `keepalive` : l'onglet passe en arrière-plan ou se ferme. */
  function flush(current: Recording, active: Config, keepalive = false) {
    const recorded = current.window
    if (recorded.buffer.length === 0) return
    const tabId = active.tab.id()
    const batch: RecordingBatch = {
      publicKey: active.publicKey,
      sdkVersion: SDK_VERSION,
      distinctId: recorded.distinctId,
      sessionId: recorded.sessionId,
      sessionStartedAt: recorded.sessionStartedAt,
      windowId: recorded.windowId,
      ...(tabId === undefined ? {} : { tabId }),
      seq: recorded.nextSeq++,
      context: currentContext(),
      events: recorded.buffer.splice(0),
      ...(recorded.customEvents.length > 0
        ? { customEvents: recorded.customEvents.splice(0) }
        : {}),
    }
    recorded.bufferBytes = 0
    active.sender.send(batch, { keepalive, sessionId: batch.sessionId })
  }

  /** Une nouvelle window de cet onglet dans `session` (à chaque chargement de page). */
  function openWindow(
    session: SharedSession,
    distinctId: string,
    createdHere: boolean,
  ): RecordedWindow {
    return {
      distinctId,
      sessionId: session.id,
      sessionStartedAt: session.startedAt,
      windowId: uuidv7(),
      nextSeq: 0,
      buffer: [],
      customEvents: [],
      bufferBytes: 0,
      createdHere,
      capped: session.capped === true,
    }
  }

  /** Cesse d'enregistrer la session de cette window (refusée par l'API pour volume). */
  function cap(recorded: RecordedWindow) {
    recorded.capped = true
    recorded.buffer.length = 0
    recorded.customEvents.length = 0
    recorded.bufferBytes = 0
  }

  /**
   * L'API refuse la session, devenue trop volumineuse : cet onglet cesse de l'enregistrer et
   * l'état partagé le signale aux autres onglets (et aux pages rechargées). L'activité reste
   * suivie : la session suivante (inactivité, 24 h, `forceNew`) est enregistrée normalement.
   */
  function onSessionTooLarge(active: Config, sessionId: string) {
    if (recording?.window.sessionId === sessionId) cap(recording.window)
    const state = active.store.read()
    const shared = state?.session
    if (state && shared?.id === sessionId && !shared.capped) {
      active.store.write({ ...state, session: { ...shared, capped: true } })
    }
  }

  /**
   * L'origine de la page n'est pas autorisée par le projet : l'enregistrement de cette page
   * s'arrête sans rien envoyer de plus (les autres onglets et l'état partagé ne sont pas touchés :
   * chaque page le découvre à son premier envoi). Un rechargement retente, au cas où le member
   * aurait ajouté l'origine entre-temps.
   */
  function onOriginNotAllowed(active: Config) {
    if (active.originRefused) return
    active.originRefused = true
    console.warn(
      `[belvdr] L'origine ${location.origin} n'est pas autorisée par ce projet : la page n'est ` +
        'pas enregistrée. Ajoute-la dans les réglages du projet (Origines autorisées).',
    )
    stopErrorMode(active, { flush: false })
    const current = recording
    if (!current) return
    current.stopped = true
    current.stop()
    current.window.buffer.length = 0
    current.window.bufferBytes = 0
    recording = null
    unwatchIfIdle()
  }

  /** Session de l'onglet encore active à l'instant `now`, d'après ce qu'il en sait. */
  function isLive(current: Recording, now: number) {
    const { sessionStartedAt: startedAt } = current.window
    return isActive({ startedAt, lastActivityAt: current.lastActivityAt }, now)
  }

  /**
   * Fait passer l'onglet dans une autre session : ce qui a été enregistré part avec l'ancienne,
   * la nouvelle window commence par un full snapshot pour se rejouer seule.
   */
  function switchTo(
    current: Recording,
    active: Config,
    identity: Identity,
    session: SharedSession,
    createdHere: boolean,
  ) {
    flush(current, active)
    current.identity = identity
    current.window = openWindow(session, identity.distinctId, createdHere)
    current.lastActivityAt = session.lastActivityAt
    record.takeFullSnapshot()
  }

  /** Démarre une nouvelle session pour tous les onglets, sous l'identité `identity`. */
  function rotate(current: Recording, active: Config, identity: Identity, now: number) {
    const session = createSession(now)
    active.store.write({ ...identity, session })
    switchTo(current, active, identity, session, true)
  }

  /** Démarre une nouvelle session, pour tous les onglets, sans changer d'identité. */
  function renew(current: Recording, active: Config, now: number) {
    rotate(current, active, current.identity, now)
  }

  /** Rejoint la session d'un autre onglet. */
  function join(current: Recording, active: Config, identity: Identity, session: SharedSession) {
    const abandoned = current.window
    if (abandoned.createdHere && abandoned.nextSeq === 0) {
      // Deux onglets ont créé une session au même instant, chacun sans voir l'écriture de
      // l'autre : celle de cet onglet n'a encore rien envoyé, elle est remplacée sans laisser de
      // session fantôme (la window garde ce qu'elle a enregistré, full snapshot compris).
      current.identity = identity
      const joined = openWindow(session, identity.distinctId, false)
      current.window = joined.capped
        ? joined
        : {
            ...joined,
            buffer: abandoned.buffer,
            customEvents: abandoned.customEvents,
            bufferBytes: abandoned.bufferBytes,
          }
      current.lastActivityAt = Math.max(current.lastActivityAt, session.lastActivityAt)
      return
    }
    switchTo(current, active, identity, session, false)
  }

  /**
   * Ajoute un event custom à la window : l'event pour l'API, et sa trace dans l'enregistrement
   * (ce qui garantit aussi un envoi, même si rien d'autre ne se passe).
   */
  function queueCustomEvent(recorded: RecordedWindow, event: CustomEvent) {
    if (recorded.capped) return
    const traced: RrwebEvent = {
      type: RRWEB_CUSTOM_EVENT,
      timestamp: event.timestamp,
      data: { tag: event.name, payload: event.properties },
    }
    recorded.buffer.push(traced)
    recorded.bufferBytes += JSON.stringify(traced).length
    recorded.customEvents.push(event)
  }

  /** Ajoute un `$identify` à la window. */
  function queueIdentify(recorded: RecordedWindow, userId: string, now: number) {
    queueCustomEvent(recorded, {
      uuid: uuidv7(now),
      name: IDENTIFY_EVENT,
      timestamp: now,
      properties: { userId },
    })
  }

  /**
   * Rattache le navigateur à `userId`. Anonyme, il garde son `distinctId` (son historique
   * revient à l'utilisateur) ; déjà identifié comme un autre utilisateur, il prend une nouvelle
   * identité et une nouvelle session. L'identification compte comme une activité (un login).
   */
  function identifyAs(current: Recording, active: Config, userId: string, now: number) {
    const { identity } = current
    if (identity.userId === userId) return
    if (identity.userId !== undefined) {
      rotate(current, active, newIdentity(now, userId), now)
    } else {
      if (!isLive(current, now)) renew(current, active, now)
      current.identity = { ...current.identity, userId }
      const state = active.store.read()
      if (state?.distinctId === current.identity.distinctId)
        active.store.write({ ...state, userId })
    }
    current.lastActivityAt = now
    queueIdentify(current.window, userId, now)
  }

  /**
   * Aligne l'onglet sur l'état partagé : arrêt demandé ailleurs, session changée par un autre
   * onglet ; publie sa dernière activité ou reprend celle des autres.
   *
   * `notified` : appelé sur notification du navigateur (événement `storage`), donc aussitôt après
   * la modification ; sinon, la modification a pu avoir lieu jusqu'à 5 s plus tôt.
   */
  function sync(current: Recording, active: Config, notified = false) {
    const state = active.store.read()
    const shared = state?.session
    if (!state || !shared) {
      // `stopSession()` dans un autre onglet (retrait du consentement). Sans notification, ce qui
      // attend d'être envoyé a pu être enregistré après le retrait : rien n'en part.
      if (!notified) {
        current.window.buffer.length = 0
        current.window.bufferBytes = 0
      }
      stopRecording(current, active)
    } else if (shared.id !== current.window.sessionId) {
      if (isActive(shared, Date.now())) join(current, active, identityOf(state), shared)
    } else {
      // Identifié dans un autre onglet (même navigateur, même distinctId).
      if (state.distinctId === current.identity.distinctId) current.identity = identityOf(state)
      // Session refusée pour volume par l'API, lors de l'envoi d'un autre onglet.
      if (shared.capped && !current.window.capped) cap(current.window)
      if (current.lastActivityAt > shared.lastActivityAt) {
        active.store.write({
          ...state,
          session: { ...shared, lastActivityAt: current.lastActivityAt },
        })
      } else {
        current.lastActivityAt = shared.lastActivityAt
      }
    }
  }

  /** Arrête l'enregistrement de cet onglet et envoie ce qu'il reste. */
  function stopRecording(current: Recording, active: Config) {
    current.stopped = true
    current.stop()
    flush(current, active)
    active.exceptions.flush()
    if (recording === current) recording = null
    unwatchIfIdle()
    // Le Mode erreurs continue : son tampon reprend.
    startBuffer(active)
  }

  /** Écoute les Exceptions de la page (une seule fois, quel que soit le mode). */
  function watch(active: Config) {
    unwatch ??= watchExceptions(window, (exception) => onPageException(active, exception))
  }

  /** Ni session ni Mode erreurs : les Exceptions ne sont plus écoutées. */
  function unwatchIfIdle() {
    if (recording || errorTracking) return
    unwatch?.()
    unwatch = null
  }

  /**
   * Une Exception de la page (ou de `captureException`) : rattachée à la session si l'onglet en
   * enregistre une, sinon envoyée par le Mode erreurs s'il tourne, sinon ignorée.
   */
  function onPageException(active: Config, exception: ParsedException) {
    if (active.originRefused) return
    const current = recording
    if (current && !current.stopped && reportException(current, active, exception)) return
    if (errorTracking) reportWithoutSession(active, exception)
  }

  /** Identité du navigateur en Mode erreurs : celle de l'état partagé, sinon celle du démarrage. */
  function errorIdentity(active: Config, started: Identity): Identity {
    const state = active.store.read()
    return state ? identityOf(state) : started
  }

  /** Exception du Mode erreurs : sans session, avec l'identité courante du navigateur. */
  function reportWithoutSession(active: Config, exception: ParsedException) {
    const started = errorTracking
    if (!started) return
    // Arrêté dans un autre onglet, même si le navigateur ne l'a pas (encore) notifié.
    if (!active.store.readErrorTracking()) return stopErrorMode(active, { flush: false })
    const identity = errorIdentity(active, started.identity)
    active.exceptions.report({
      exception,
      timestamp: Date.now(),
      url: location.href,
      distinctId: identity.distinctId,
      ...(identity.userId === undefined ? {} : { userId: identity.userId }),
    })
  }

  /**
   * Options d'enregistrement de rrweb, communes aux sessions et aux Extraits : même masquage dans
   * le navigateur, mêmes plugins console et réseau.
   */
  function recordOptions(active: Config) {
    return {
      // Masquage dans le navigateur, avant tout envoi : toutes les saisies, le texte des
      // éléments `.belvdr-mask` ; les `.belvdr-block` ne sont pas enregistrés du tout.
      maskAllInputs: true,
      maskTextClass: MASK_CLASS,
      blockClass: BLOCK_CLASS,
      plugins: [
        // Console (messages, exceptions non gérées), arguments masqués si `maskConsole`.
        consolePlugin({ mask: active.maskConsole }),
        // Réseau (hors API belvdr), URL nettoyées, corps des requêtes en erreur sur demande.
        ...(active.captureNetwork
          ? [
              networkPlugin({
                apiHost: active.apiHost,
                maskUrl: active.maskNetworkUrl,
                captureErrorBodies: active.captureErrorBodies,
                maskBody: active.maskNetworkBody,
              }),
            ]
          : []),
      ],
    }
  }

  /** Envoie un Extrait terminé, découpé en batchs `(extraitId, seq)` comme les enregistrements. */
  function sendExtrait(active: Config, extrait: Extrait) {
    const distinctId = extraitOwners.get(extrait.id)
    extraitOwners.delete(extrait.id)
    if (distinctId === undefined || active.originRefused || extrait.events.length === 0) return
    const keepalive = document.visibilityState === 'hidden'
    let seq = 0
    let events: RrwebEvent[] = []
    let size = 0
    const send = () => {
      const batch: ExtraitBatch = {
        publicKey: active.publicKey,
        sdkVersion: SDK_VERSION,
        distinctId,
        extraitId: extrait.id,
        seq: seq++,
        context: currentContext(),
        events,
      }
      active.extraitSender.send(batch, { keepalive })
      events = []
      size = 0
    }
    for (const event of extrait.events) {
      events.push(event)
      size += JSON.stringify(event).length
      if (size >= FLUSH_THRESHOLD_BYTES) send()
    }
    if (events.length > 0) send()
  }

  /**
   * Démarre le tampon des Extraits : un enregistrement rrweb de la page, gardé en mémoire, dont
   * rien ne part tant qu'aucune Exception ne survient. Jamais pendant une session complète.
   */
  function startBuffer(active: Config) {
    const current = errorTracking
    if (!current || current.buffer || recording || active.originRefused) return
    const extraits = createExtraitRecorder({
      takeFullSnapshot: () => record.takeFullSnapshot(true),
      deliver: (extrait) => sendExtrait(active, extrait),
    })
    const stopRecord = record({
      emit: (event) => extraits.push(event as RrwebEvent),
      ...recordOptions(active),
    })
    current.buffer = {
      extraits,
      stop: () => {
        stopRecord?.()
        extraits.stop()
      },
    }
  }

  /** Arrête le tampon des Extraits (session complète, arrêt du Mode erreurs). */
  function stopBuffer() {
    const buffer = errorTracking?.buffer
    if (!buffer || !errorTracking) return
    errorTracking.buffer = null
    buffer.stop()
  }

  /** L'Extrait d'une Exception du Mode erreurs, au moment de son envoi. */
  function extraitFor(distinctId: string, timestamp: number): string | undefined {
    const id = errorTracking?.buffer?.extraits.capture(timestamp) ?? null
    if (id === null) return undefined
    extraitOwners.set(id, distinctId)
    return id
  }

  /** Arrête le Mode erreurs de cet onglet ; `flush` : les occurrences écartées en attente partent. */
  function stopErrorMode(active: Config, { flush: flushPending }: { flush: boolean }) {
    const current = errorTracking
    if (!current) return
    stopBuffer()
    errorTracking = null
    current.stop()
    if (flushPending) active.exceptions.flush()
    unwatchIfIdle()
  }

  /**
   * Identification en Mode erreurs, sans session dans cet onglet : l'identité partagée change
   * (anonyme : le navigateur revient à `userId` ; un autre utilisateur : nouvelle identité, jamais
   * de fusion), les Exceptions suivantes portent `userId`, et le prochain `startSession()` émettra
   * le `$identify`.
   */
  function identifyWithoutSession(active: Config, userId: string, now: number) {
    const state = active.store.read()
    if (state?.userId === undefined) {
      const identity = state ? identityOf(state) : newIdentity(now)
      active.store.write({ ...identity, userId, session: state?.session ?? null })
    } else if (state.userId !== userId) {
      // Comme `reset()` : une session en cours dans un autre onglet laisse place à une nouvelle.
      const session = state.session && isActive(state.session, now) ? createSession(now) : null
      active.store.write({ ...newIdentity(now, userId), session })
    }
    identifiedWithoutSession = true
  }

  /**
   * Une Exception de la page, rattachée à la session et à la window si la session est active
   * (sinon, l'Exception part seule) : elle part aussitôt, après ses garde-fous. Renvoie `false` si
   * l'onglet n'enregistre plus (session arrêtée dans un autre onglet).
   */
  function reportException(
    current: Recording,
    active: Config,
    exception: ParsedException,
  ): boolean {
    if (current.stopped || active.originRefused) return false
    const now = Date.now()
    // Session expirée pour cet onglet : un autre onglet a peut-être eu de l'activité entre-temps.
    if (!isLive(current, now)) sync(current, active)
    if (current.stopped) return false
    const recorded = current.window
    const { userId } = current.identity
    active.exceptions.report({
      exception,
      timestamp: now,
      url: location.href,
      distinctId: recorded.distinctId,
      ...(userId === undefined ? {} : { userId }),
      ...(isLive(current, now)
        ? { session: { id: recorded.sessionId, windowId: recorded.windowId } }
        : {}),
    })
    return true
  }

  function onEvent(current: Recording, active: Config, event: RrwebEvent) {
    if (current.stopped) return
    const now = event.timestamp
    // Session expirée pour cet onglet : un autre onglet a peut-être eu de l'activité entre-temps.
    if (!isLive(current, now)) sync(current, active)
    if (current.stopped) return
    if (isInteraction(event)) {
      if (!isLive(current, now)) renew(current, active, now)
      current.lastActivityAt = now
    } else if (!isLive(current, now)) {
      // Session expirée : rien n'est enregistré jusqu'à la prochaine interaction.
      return
    }
    const recorded = current.window
    if (recorded.capped) return
    recorded.buffer.push(event)
    recorded.bufferBytes += JSON.stringify(event).length
    if (recorded.bufferBytes >= FLUSH_THRESHOLD_BYTES) flush(current, active)
  }

  return {
    init(publicKey, options) {
      const transport = options.transport ?? fetchTransport
      config = {
        publicKey,
        apiHost: options.apiHost,
        sender: createSender({
          endpoint: new URL(RECORDINGS_PATH, options.apiHost).href,
          transport: options.transport ?? fetchTransport,
          onSessionTooLarge: (sessionId) => {
            if (config) onSessionTooLarge(config, sessionId)
          },
          onOriginNotAllowed: () => {
            if (config) onOriginNotAllowed(config)
          },
        }),
        exceptions: createExceptionReporter({
          publicKey,
          sender: createSender({
            endpoint: new URL(EXCEPTIONS_PATH, options.apiHost).href,
            transport,
            onOriginNotAllowed: () => {
              if (config) onOriginNotAllowed(config)
            },
          }),
          maskMessage: options.maskConsole === true,
          beforeSend: options.beforeSendException,
          context: exceptionContext,
          refused: () => config?.originRefused === true,
          extraitFor: (occurrence) => extraitFor(occurrence.distinctId, occurrence.timestamp),
          decideExtrait: (extraitId, decision) =>
            errorTracking?.buffer?.extraits.decide(extraitId, decision),
        }),
        extraitSender: createSender({
          endpoint: new URL(EXTRAITS_PATH, options.apiHost).href,
          transport,
          onOriginNotAllowed: () => {
            if (config) onOriginNotAllowed(config)
          },
        }),
        store: localSessionStore(publicKey),
        maskConsole: options.maskConsole === true,
        captureNetwork: options.captureNetwork !== false,
        captureInteractions: options.captureInteractions !== false,
        maskNetworkUrl: options.maskNetworkUrl,
        captureErrorBodies: options.captureErrorBodies === true,
        maskNetworkBody: options.maskNetworkBody,
        originRefused: false,
        tab: browserTab(publicKey),
      }
    },

    startSession(options = {}) {
      if (!config || config.originRefused) return
      const active = config
      let userId = options.userId ?? pendingUserId
      // Utilisateur identifié en Mode erreurs : son `$identify` part avec cette session.
      const identifyAnyway = identifiedWithoutSession && userId === pendingUserId
      pendingUserId = undefined
      identifiedWithoutSession = false
      if (userId !== undefined && !isValidUserId(userId)) {
        warnInvalidUserId(userId)
        userId = undefined
      }
      if (recording) {
        if (options.forceNew) renew(recording, active, Date.now())
        if (userId !== undefined) identifyAs(recording, active, userId, Date.now())
        return
      }
      const now = Date.now()
      const stored = active.store.read()
      let identity = stored ? identityOf(stored) : newIdentity(now)
      const sameUser = userId !== undefined && identity.userId === userId
      const identifying = userId !== undefined && (!sameUser || identifyAnyway)
      // Navigateur déjà identifié comme un autre utilisateur : nouvelle identité, jamais de fusion.
      const anotherUser = userId !== undefined && identity.userId !== undefined && !sameUser
      if (anotherUser) identity = newIdentity(now, userId)
      else if (userId !== undefined) identity = { ...identity, userId }
      const previous = options.forceNew || anotherUser ? null : stored?.session
      const resumed = previous && isActive(previous, now) ? previous : null
      const session = resumed ? { ...resumed, lastActivityAt: now } : createSession(now)
      active.store.write({ ...identity, session })
      // Une session complète a son propre enregistrement : pas de tampon d'Extraits pendant.
      stopBuffer()
      // L'onglet est réservé dès le démarrage : une copie de cet onglet (duplication) le saura.
      active.tab.id()
      const current: Recording = {
        identity,
        window: openWindow(session, identity.distinctId, !resumed),
        lastActivityAt: now,
        stopped: false,
        stop: () => {},
      }
      recording = current
      const rrwebOptions = recordOptions(active)
      const stopRecorder = record({
        emit: (event) => onEvent(current, active, event as RrwebEvent),
        ...rrwebOptions,
        // Faits : seulement pendant une session complète, jamais dans le tampon des Extraits.
        plugins: [
          ...rrwebOptions.plugins,
          ...(active.captureInteractions ? [interactionsPlugin()] : []),
        ],
        checkoutEveryNms: FULL_SNAPSHOT_INTERVAL_MS,
      })
      // Exceptions de la page, envoyées tant que l'onglet enregistre.
      watch(active)
      if (identifying && userId !== undefined) queueIdentify(current.window, userId, now)
      /** Aligne l'onglet sur l'état partagé (et y publie son activité), puis envoie le tampon. */
      const syncAndFlush = (keepalive: boolean) => {
        sync(current, active)
        if (!current.stopped) flush(current, active, keepalive)
      }
      const interval = setInterval(() => syncAndFlush(false), FLUSH_INTERVAL_MS)
      // Onglet masqué (changement d'onglet, application en arrière-plan, fermeture) : c'est peut-être
      // le dernier moment où la page peut envoyer quelque chose. `pagehide` couvre les navigateurs
      // qui ne signalent pas le masquage à la fermeture.
      const onHide = () => {
        if (current.stopped) return
        syncAndFlush(true)
        active.exceptions.flush()
      }
      const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') onHide()
      }
      document.addEventListener('visibilitychange', onVisibilityChange)
      window.addEventListener('pagehide', onHide)
      // Un autre onglet a modifié l'état partagé (arrêt, nouvelle session) : on le suit aussitôt.
      const onStorage = () => {
        if (!current.stopped) sync(current, active, true)
      }
      window.addEventListener('storage', onStorage)
      current.stop = () => {
        clearInterval(interval)
        window.removeEventListener('storage', onStorage)
        document.removeEventListener('visibilitychange', onVisibilityChange)
        window.removeEventListener('pagehide', onHide)
        stopRecorder?.()
      }
    },

    stopSession() {
      if (!config) return
      if (recording) stopRecording(recording, config)
      // L'état de session effacé, les autres onglets s'arrêtent à leur tour ; l'identité reste.
      const state = config.store.read()
      if (state?.session) config.store.write({ ...identityOf(state), session: null })
    },

    startErrorTracking() {
      if (!config || config.originRefused) return
      const active = config
      active.store.writeErrorTracking(true)
      // Le navigateur a besoin d'une identité : celle de l'état partagé, ou une nouvelle.
      if (!active.store.read()) active.store.write({ ...newIdentity(Date.now()), session: null })
      if (pendingUserId !== undefined && !recording) {
        identifyWithoutSession(active, pendingUserId, Date.now())
      }
      if (errorTracking) return
      // Arrêt dans un autre onglet : suivi aussitôt. Page masquée ou fermée : les occurrences
      // écartées en attente partent.
      const onStorage = () => {
        if (!active.store.readErrorTracking()) stopErrorMode(active, { flush: false })
      }
      const onHide = () => active.exceptions.flush()
      const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') onHide()
      }
      // Page déchargée : l'Extrait en cours part au mieux, tel quel ; le tampon reprend si la page
      // revient du cache de navigation.
      const onPageHide = () => {
        onHide()
        stopBuffer()
      }
      const onPageShow = (event: PageTransitionEvent) => {
        if (event.persisted) startBuffer(active)
      }
      window.addEventListener('storage', onStorage)
      window.addEventListener('pagehide', onPageHide)
      window.addEventListener('pageshow', onPageShow)
      document.addEventListener('visibilitychange', onVisibilityChange)
      errorTracking = {
        identity: errorIdentity(active, newIdentity(Date.now())),
        stop: () => {
          window.removeEventListener('storage', onStorage)
          window.removeEventListener('pagehide', onPageHide)
          window.removeEventListener('pageshow', onPageShow)
          document.removeEventListener('visibilitychange', onVisibilityChange)
        },
        buffer: null,
      }
      watch(active)
      startBuffer(active)
    },

    stopErrorTracking() {
      if (!config) return
      // L'état partagé effacé, les autres onglets s'arrêtent à leur tour.
      config.store.writeErrorTracking(false)
      stopErrorMode(config, { flush: true })
    },

    identify(userId) {
      if (!isValidUserId(userId)) return warnInvalidUserId(userId)
      if (!config || config.originRefused) return
      const current = recording
      // Sans enregistrement dans cet onglet, l'identification attend le prochain
      // `startSession()` ; en Mode erreurs, elle s'applique aussitôt aux Exceptions.
      if (current) sync(current, config)
      if (!current || current.stopped) {
        pendingUserId = userId
        if (errorTracking) identifyWithoutSession(config, userId, Date.now())
        return
      }
      identifyAs(current, config, userId, Date.now())
    },

    capture(name, properties) {
      const refusedName = refusedEventName(name)
      if (refusedName !== null) return warnRefusedEvent(name, refusedName)
      const normalized = captureProperties(properties)
      if ('refused' in normalized)
        return warnRefusedEvent(name, `propriétés refusées (${normalized.refused})`)
      if (!config || config.originRefused) return
      const active = config
      const current = recording
      if (!current || current.stopped) return
      const now = Date.now()
      // Session expirée pour cet onglet : un autre onglet a peut-être eu de l'activité entre-temps.
      if (!isLive(current, now)) sync(current, active)
      // Un event n'est pas une interaction de l'utilisateur : il ne prolonge pas une session
      // expirée, rien n'est enregistré jusqu'à la prochaine interaction.
      if (current.stopped || !isLive(current, now)) return
      queueCustomEvent(current.window, {
        uuid: uuidv7(now),
        name,
        timestamp: now,
        properties: normalized,
        url: location.href,
      })
      if (current.window.customEvents.length >= MAX_CUSTOM_EVENTS_PER_BATCH) flush(current, active)
    },

    captureException(error) {
      try {
        if (!config || (!recording && !errorTracking)) return
        onPageException(
          config,
          parseThrown(error, { handled: true, mechanism: 'captureException' }),
        )
      } catch {
        // Appelé depuis le code d'erreur de l'application : ne jamais y ajouter une erreur.
      }
    },

    reset() {
      pendingUserId = undefined
      identifiedWithoutSession = false
      if (!config) return
      const now = Date.now()
      const identity = newIdentity(now)
      const current = recording
      if (current) sync(current, config)
      if (current && !current.stopped) {
        rotate(current, config, identity, now)
        return
      }
      // Cet onglet n'enregistre pas : l'identité partagée change quand même (logout), et une
      // session en cours dans d'autres onglets y laisse place à une nouvelle.
      const state = config.store.read()
      if (!state) return
      const session = state.session && isActive(state.session, now) ? createSession(now) : null
      config.store.write({ ...identity, session })
    },
  }
}
